/**
 * StoreLifecycleActions — "Report closed" and "Report moved" on a store (rule R6).
 *
 * Closing takes effect at once: the store leaves the map and visit plans, its
 * visits are cancelled and the area manager is told. A move is a note for an
 * admin (reps can't edit a location); it arrives in the admin's Tasks.
 * After a closure the page is asked to refresh via `northai:store-changed`.
 */
import React, { useState } from 'react';
import { MapPinOff, Store } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';

const cfg = { withCredentials: true };
const REASONS = [
  ['closed_for_good', 'Closed for good'],
  ['moved_away', 'Moved away (left our area)'],
  ['merged', 'Merged with another store'],
  ['other', 'Other'],
];
const errorText = (e, fallback) => {
  const d = e.response?.data?.detail;
  return (d && d.message) || (typeof d === 'string' ? d : null) || fallback;
};
const input = 'w-full text-sm bg-gray-800 border border-gray-600 rounded-lg px-3 py-2';

const StoreLifecycleActions = ({ entity, onClosed }) => {
  const [mode, setMode] = useState(null); // 'close' | 'moved'
  const [f, setF] = useState({ reason: 'closed_for_good', closed_on: '', note: '', new_address: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const kind = entity.entity_type === 'customer' ? 'customer' : 'lead';
  const id = Number(String(entity.global_id || '').split('_').pop()) || entity.id;
  const url = `${API_BASE_URL}/api/stores/${kind}/${id}`;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      if (mode === 'close') {
        await axios.post(`${url}/close`, {
          reason: f.reason, closed_on: f.closed_on || null, note: f.note || null,
        }, cfg);
        setMsg('Reported closed. It is off the map and your plans.');
        window.dispatchEvent(new CustomEvent('northai:store-changed'));
        onClosed?.();
      } else {
        await axios.post(`${url}/report-moved`, { new_address: f.new_address || null, note: f.note || null }, cfg);
        setMsg('Sent to an admin to update the location.');
      }
      setMode(null);
    } catch (e2) {
      setErr(errorText(e2, 'That did not work.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3">
      {msg && <p className="text-xs text-emerald-300 mb-2">{msg}</p>}
      {err && <p className="text-xs text-red-300 mb-2">{err}</p>}
      {!mode ? (
        <div className="flex gap-3 text-xs">
          <button onClick={() => { setMode('close'); setMsg(''); }} className="inline-flex items-center gap-1 text-gray-400 hover:text-red-300">
            <Store size={13} /> Report closed
          </button>
          <button onClick={() => { setMode('moved'); setMsg(''); }} className="inline-flex items-center gap-1 text-gray-400 hover:text-gray-200">
            <MapPinOff size={13} /> Report moved
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="border border-gray-700 rounded-lg p-3 space-y-2">
          <p className="text-sm font-medium text-gray-200">
            {mode === 'close' ? 'Report this store closed' : 'Report that this store moved'}
          </p>
          {mode === 'close' ? (
            <>
              <select className={input} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}>
                {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <label className="block text-xs text-gray-400">Closed on (optional)
                <input type="date" className={input} value={f.closed_on} max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setF({ ...f, closed_on: e.target.value })} />
              </label>
              <p className="text-xs text-amber-300">Takes effect now: its planned visits are cancelled and it leaves the map.</p>
            </>
          ) : (
            <input className={input} placeholder="New address (if you know it)" value={f.new_address}
              onChange={(e) => setF({ ...f, new_address: e.target.value })} maxLength={500} />
          )}
          <input className={input} placeholder="Note (optional)" value={f.note}
            onChange={(e) => setF({ ...f, note: e.target.value })} maxLength={2000} />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setMode(null)} className="text-sm px-3 py-1.5 rounded-lg border border-gray-600 text-gray-300">Cancel</button>
            <button disabled={busy} className={`text-sm px-3 py-1.5 rounded-lg text-white disabled:opacity-50 ${mode === 'close' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
              {mode === 'close' ? 'Report closed' : 'Send to admin'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default StoreLifecycleActions;
